import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  message,
  Typography,
  Card,
  Tag,
  Spin,
  Empty,
  Divider,
  Input,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAuth from "../hooks/useAuth";
import instance from "../api/cmsapi";
import QuillHtmlDisplay from "./QuillHtmlDisplay";
import PetitionDocumentPanel from "./PetitionDocumentPanel";
import { quillFormats, quillModules } from "../utils/quillEditorConfig";
import {
  getPetitionDecision,
  getPetitionStatusMeta,
  isAcceptedPetition,
  isDecisionOnlyCommitteeResponse,
  buildPetitionDecisionHtml,
  buildCommitteePetitionTableId,
} from "../utils/petitionHelpers";
import { isSuperuserUser } from "../utils/dashboardScope";

const DPAT_PRIMARY = "#16325a";
const TYPE = "DPAT";
const PETITION_DECISION_USERNAMES = new Set(["fkwakye", "ofsam"]);

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function formatCommentDate(date) {
  if (!date) return "—";
  if (Array.isArray(date)) return date.filter((x) => x != null).join("/");
  return String(date);
}

function normalizeUserRole(role) {
  return String(role || "").replace(/\s+/g, "_").toUpperCase();
}

function findDistrictPetition(rows, districtId, districtPetitionTableId) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    list.find(
      (row) =>
        row.tableCommented === districtPetitionTableId &&
        String(row.districtId) === String(districtId) &&
        normalizeUserRole(row.userRole) === "DDUSER"
    ) ??
    list.find(
      (row) =>
        row.tableCommented === districtPetitionTableId &&
        String(row.districtId) === String(districtId)
    ) ??
    null
  );
}

function PetitionCommittee({
  year,
  districtId,
  districtName = "",
  assessmentStatus,
  onRequestCloseAssessment,
}) {
  const { user } = useAuth();
  const [editorContent, setEditorContent] = useState("");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [existingCommentId, setExistingCommentId] = useState(null);
  const [districtPetition, setDistrictPetition] = useState(null);
  const [committeeResponse, setCommitteeResponse] = useState(null);
  const [isEditingResponse, setIsEditingResponse] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState("");
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [submittingDecision, setSubmittingDecision] = useState(false);

  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const normalizedUsername = String(currentUsername || "").trim().toLowerCase();
  const isSuperuser = isSuperuserUser(user);
  const canDecidePetition = Boolean(
    isSuperuser ||
      user?.user?.userRoles?.some((role) => role.name === "DPAT PETITION COMMITTEE") ||
      PETITION_DECISION_USERNAMES.has(normalizedUsername)
  );
  // Keep existing name used throughout this component for committee UI access
  const isPetitionCommittee = canDecidePetition;

  const districtPetitionTableId = useMemo(
    () => (year != null && districtId != null ? `${year}petition${districtId}` : null),
    [year, districtId]
  );

  const committeeTableId = useMemo(
    () => (year != null && districtId != null ? `${year}petitioncommittee${districtId}` : null),
    [year, districtId]
  );

  const petitionAccepted = isAcceptedPetition(committeeResponse);
  const petitionDecision = getPetitionDecision(committeeResponse);
  const petitionDecisionMeta = petitionDecision ? getPetitionStatusMeta(petitionDecision) : null;

  const canRespond = useCallback(() => {
    if (!isPetitionCommittee || !assessmentStatus || !districtPetition || !petitionAccepted) {
      return false;
    }
    if (!committeeResponse || isDecisionOnlyCommitteeResponse(committeeResponse)) {
      return true;
    }
    return committeeResponse.username === currentUsername;
  }, [
    isPetitionCommittee,
    assessmentStatus,
    districtPetition,
    petitionAccepted,
    committeeResponse,
    currentUsername,
  ]);

  const committeeResponsePending =
    petitionAccepted &&
    (!committeeResponse || isDecisionOnlyCommitteeResponse(committeeResponse));

  const showResponseEditor =
    canRespond() && !fetchLoading && (committeeResponsePending || isEditingResponse);

  const showSubmittedResponse =
    Boolean(committeeResponse) &&
    !isEditingResponse &&
    !isDecisionOnlyCommitteeResponse(committeeResponse);

  useEffect(() => {
    setEditorContent("");
    setExistingCommentId(null);
    setDistrictPetition(null);
    setCommitteeResponse(null);
    setIsEditingResponse(false);
    setFetchLoading(true);
  }, [districtId, year]);


  useEffect(() => {
    let cancelled = false;

    const fetchPetitionData = async () => {
      if (!districtId || year == null || !districtPetitionTableId || !committeeTableId) {
        if (!cancelled) setFetchLoading(false);
        return;
      }

      try {
        setFetchLoading(true);

        const [petitionResult, committeeResult] = await Promise.allSettled([
          instance.get(`comments/table/${districtId}/${year}/${TYPE}/${districtPetitionTableId}`),
          instance.get(`comments/table/${districtId}/${year}/${TYPE}/${committeeTableId}`),
        ]);

        if (cancelled) return;

        let petitionData =
          petitionResult.status === "fulfilled" ? petitionResult.value?.data : [];
        const committeeData =
          committeeResult.status === "fulfilled" ? committeeResult.value?.data : [];

        let petition = findDistrictPetition(
          petitionData,
          districtId,
          districtPetitionTableId
        );

        if (!petition) {
          try {
            const tablesRes = await instance.get(
              `comments/tables/${districtId}/${year}/${TYPE}`
            );
            if (!cancelled) {
              petition = findDistrictPetition(
                tablesRes.data,
                districtId,
                districtPetitionTableId
              );
            }
          } catch {
            // Fall back to empty state if tables endpoint is unavailable.
          }
        }

        const committee =
          (Array.isArray(committeeData) ? committeeData : []).find(
            (row) =>
              normalizeUserRole(row.userRole) === "DPC" &&
              String(row.districtId) === String(districtId) &&
              row.tableCommented === committeeTableId
          ) ?? null;

        setDistrictPetition(petition);
        setCommitteeResponse(committee);

        if (committee) {
          setEditorContent(
            isDecisionOnlyCommitteeResponse(committee) ? "" : committee.comments || ""
          );
          setExistingCommentId(committee.id);
        }
      } catch (error) {
        console.error("Failed to fetch petition committee data:", error);
        if (!cancelled) message.error("Could not load petition data for this district.");
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    };

    fetchPetitionData();
    return () => {
      cancelled = true;
    };
  }, [districtId, year, districtPetitionTableId, committeeTableId]);

  
  const handleSaveResponse = async (isDraft = false) => {
    if (stripHtml(editorContent) === "") {
      message.error("Response cannot be empty");
      return;
    }
    if (!currentUsername || !districtId || year == null) {
      message.error("User or district information is missing");
      return;
    }
    if (!isPetitionCommittee) {
      message.error("Only the Complaints Resolution Committee can respond here");
      return;
    }
    if (!canRespond()) {
      message.error("You cannot submit a response for this district.");
      return;
    }

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingCommentId || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: "DPC",
      type: TYPE,
      districtId,
      year,
      tableCommented: committeeTableId,
      comments: editorContent,
      commentDate,
      updateDate: commentDate,
      dddpDataDate: commentDate,
      dddpData: {
        ...(committeeResponse?.dddpData || {}),
        ...(isDraft ? {} : { committeeResponseSubmitted: true }),
      },
    };

    try {
      setSaveLoading(true);
      if (existingCommentId) {
        const response = await instance.put(`comments/${existingCommentId}`, payload);
        setCommitteeResponse(response.data);
        setIsEditingResponse(false);
        message.success(isDraft ? "Draft updated successfully" : "Response submitted successfully");
        if (!isDraft) {
          onRequestCloseAssessment?.();
        }
      } else {
        const endpoint = isDraft ? "comments/draft" : "comments";
        const response = await instance.post(endpoint, payload);
        setCommitteeResponse(response.data);
        setExistingCommentId(response.data.id);
        setIsEditingResponse(false);
        message.success(isDraft ? "Draft saved successfully" : "Response submitted successfully");
        if (!isDraft) {
          onRequestCloseAssessment?.();
        }
      }
    } catch (error) {
      console.error("Failed to save committee response:", error);
      message.error(
        `Failed to save response: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePetitionDecision = (decision) => {
    setPendingDecision(decision);
    setDecisionModalVisible(true);
  };

  const submitDecision = async () => {
    if (!pendingDecision) return;

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const committeeTableId = buildCommitteePetitionTableId(year, districtId);
    
    const payload = {
      id: committeeResponse?.id || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: "DPC",
      type: TYPE,
      districtId,
      year,
      tableCommented: committeeTableId,
      comments: buildPetitionDecisionHtml(pendingDecision, decisionNotes),
      commentDate: committeeResponse?.commentDate || commentDate,
      updateDate: commentDate,
      dddpDataDate: commentDate,
      dddpData: { petitionDecision: pendingDecision },
    };

    try {
      setSubmittingDecision(true);
      let savedResponse;
      
      if (committeeResponse?.id) {
        const response = await instance.put(`comments/${committeeResponse.id}`, payload);
        savedResponse = response.data;
      } else {
        const response = await instance.post("comments", payload);
        savedResponse = response.data;
      }

      setCommitteeResponse(savedResponse);
      setExistingCommentId(savedResponse.id);
      message.success(
        pendingDecision === "accepted" 
          ? "Petition accepted successfully" 
          : "Petition rejected successfully"
      );
      setDecisionModalVisible(false);
      setDecisionNotes("");
      setPendingDecision(null);
    } catch (error) {
      console.error("Failed to submit petition decision:", error);
      message.error(
        `Failed to submit decision: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setSubmittingDecision(false);
    }
  };

  if (!fetchLoading && !isPetitionCommittee && (!districtPetition || !petitionAccepted)) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8">
      <Card className="shadow-sm" bordered styles={{ body: { padding: "28px 24px 32px" } }}>
        <div className="mb-6 text-center">
          <div
            className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(22, 50, 90, 0.08)" }}
          >
            <TeamOutlined style={{ fontSize: 22, color: DPAT_PRIMARY }} />
          </div>
          <Typography.Title
            level={3}
            style={{ margin: 0, color: DPAT_PRIMARY, letterSpacing: "0.04em" }}
          >
            COMPLAINTS RESOLUTION COMMITTEE
          </Typography.Title>
          <Typography.Paragraph
            type="secondary"
            style={{ margin: "8px 0 0", maxWidth: 520, marginInline: "auto" }}
          >
            Review the district petition and submit an official Complaints Resolution Committee response.
          </Typography.Paragraph>
        </div>

        {fetchLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Spin size="large" tip="Loading petition data…" />
          </div>
        ) : (
          <div className="mx-auto flex w-full flex-col gap-8">
            {/* District petition */}
            <section className="w-full">
              <div className="mb-3 flex items-center justify-center gap-2">
                <FileTextOutlined style={{ color: DPAT_PRIMARY }} />
                <Typography.Title level={5} style={{ margin: 0 }}>
                  District petition
                </Typography.Title>
              </div>

              {!districtPetition ? (
                <Card
                  bordered={false}
                  className="text-center"
                  style={{ background: "#f8fafc", border: "1px dashed #d4dce6" }}
                >
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No petition has been submitted for this district yet."
                  />
                </Card>
              ) : !petitionAccepted && !isPetitionCommittee ? (
                <Card
                  bordered={false}
                  className="text-center"
                  style={{ background: "#f8fafc", border: "1px dashed #d4dce6" }}
                >
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      petitionDecision === "rejected"
                        ? "This district petition was rejected. Only accepted petitions are shown here."
                        : "This district petition is pending review. Only accepted petitions are shown here."
                    }
                  />
                </Card>
              ) : (
                <div
                  className="w-full rounded-xl border px-5 py-5"
                  style={{ borderColor: "#e6ebf1", background: "#fafbfc" }}
                >
                  <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
                    <Tag color="blue" icon={<FileTextOutlined />}>
                      District petition received
                    </Tag>
                    {petitionDecisionMeta && (
                      <Tag className={petitionDecisionMeta.badgeClass}>{petitionDecisionMeta.label}</Tag>
                    )}
                    {!petitionDecision && isPetitionCommittee && (
                      <Tag color="warning">Pending decision</Tag>
                    )}
                  </div>
                  <QuillHtmlDisplay html={districtPetition.comments} />
                  <PetitionDocumentPanel
                    districtId={districtId}
                    year={year}
                    districtName={districtName}
                    petitionComment={districtPetition}
                  />
                  <Typography.Text
                    type="secondary"
                    style={{ display: "block", marginTop: 12, textAlign: "right", fontSize: 12 }}
                  >
                    Submitted {formatCommentDate(districtPetition.commentDate)}
                  </Typography.Text>
                </div>
              )}
            </section>

            {/* Accept/Reject Decision Buttons — Petition Committee + Superuser */}
            {canDecidePetition && districtPetition && !petitionDecision && (
              <section className="w-full">
                <div
                  className="w-full rounded-xl border border-dashed px-6 py-5"
                  style={{ borderColor: "#faad14", background: "#fffbe6" }}
                >
                  <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8, textAlign: "center" }}>
                    Review petition decision
                  </Typography.Title>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 16, textAlign: "center", fontSize: 13 }}
                  >
                    Accept or reject this petition. You can provide an optional response after making your decision.
                  </Typography.Paragraph>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button
                      danger
                      size="large"
                      icon={<CloseCircleOutlined />}
                      onClick={() => handlePetitionDecision("rejected")}
                      disabled={submittingDecision}
                      style={{ minWidth: 160 }}
                    >
                      Reject petition
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handlePetitionDecision("accepted")}
                      disabled={submittingDecision}
                      style={{
                        backgroundColor: DPAT_PRIMARY,
                        borderColor: DPAT_PRIMARY,
                        minWidth: 160,
                      }}
                    >
                      Accept petition
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {(committeeResponse || (isPetitionCommittee && districtPetition && petitionAccepted)) && (
              <>
                <Divider style={{ margin: 0 }} />

                {/* Committee response */}
                <section className="w-full">
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <CommentOutlined style={{ color: DPAT_PRIMARY }} />
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Committee response
                    </Typography.Title>
                  </div>

                  {showResponseEditor && assessmentStatus && (
                    <div className="w-full">
                      <div className="mb-3 text-center">
                        <Tag color="processing">
                          {isDecisionOnlyCommitteeResponse(committeeResponse)
                            ? "Write committee response"
                            : committeeResponse
                              ? "Editing response"
                              : "Draft response"}
                        </Tag>
                      </div>
                      <div className="w-full overflow-hidden rounded-lg border border-[#e6ebf1] bg-white">
                        <ReactQuill
                          value={editorContent}
                          onChange={setEditorContent}
                          modules={quillModules}
                          formats={quillFormats}
                          className="petition-quill-editor"
                          style={{ height: "420px", marginBottom: "50px", width: "100%" }}
                          readOnly={saveLoading}
                        />
                      </div>
                      <div className="mt-4 flex flex-wrap justify-center gap-3">
                        {/* <Button
                          size="large"
                          onClick={() => handleSaveResponse(true)}
                          loading={saveLoading}
                          disabled={!canRespond()}
                        >
                          Save draft
                        </Button> */}
                        <Button
                          type="primary"
                          size="large"
                          onClick={() => handleSaveResponse(false)}
                          loading={saveLoading}
                          disabled={!canRespond()}
                          style={{
                            backgroundColor: DPAT_PRIMARY,
                            borderColor: DPAT_PRIMARY,
                            minWidth: 200,
                          }}
                        >
                          Submit response
                        </Button>
                      </div>
                    </div>
                  )}

                  {!assessmentStatus && !committeeResponse && districtPetition && (
                    <Card
                      bordered={false}
                      className="text-center"
                      style={{ background: "#f8fafc", border: "1px dashed #d4dce6" }}
                    >
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No committee response was submitted before this assessment was closed."
                      />
                    </Card>
                  )}

                  {showSubmittedResponse && committeeResponse && (
                    <div
                      className="w-full rounded-xl border px-5 py-5"
                      style={{ borderColor: "#d9f7be", background: "#f6ffed" }}
                    >
                      <div className="mb-4 flex flex-wrap items-center justify-center gap-3">
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Complaints Resolution Committee — Response submitted
                        </Tag>
                        {assessmentStatus && committeeResponse.username === currentUsername && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => {
                              setEditorContent(committeeResponse.comments || "");
                              setExistingCommentId(committeeResponse.id);
                              setIsEditingResponse(true);
                            }}
                          >
                            Edit response
                          </Button>
                        )}
                      </div>
                      <QuillHtmlDisplay html={committeeResponse.comments} />
                      <Typography.Text
                        type="secondary"
                        style={{
                          display: "block",
                          marginTop: 12,
                          textAlign: "right",
                          fontSize: 12,
                        }}
                      >
                        {formatCommentDate(committeeResponse.updateDate || committeeResponse.commentDate)}
                      </Typography.Text>
                    </div>
                  )}
                </section>
              </>
            )}

          </div>
        )}
      </Card>

      <Modal
        open={decisionModalVisible}
        onCancel={() => {
          setDecisionModalVisible(false);
          setDecisionNotes("");
          setPendingDecision(null);
        }}
        footer={null}
        width={600}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 22 }} />
            <span>
              {pendingDecision === "accepted" ? "Accept petition" : "Reject petition"}
            </span>
          </div>
        }
        destroyOnClose
      >
        <div style={{ padding: "16px 0" }}>
          <Typography.Paragraph style={{ marginBottom: 16 }}>
            {pendingDecision === "accepted"
              ? "You are about to accept this district petition. You can provide optional notes below."
              : "You are about to reject this district petition. You can provide optional notes explaining the reason."}
          </Typography.Paragraph>
          <Input.TextArea
            rows={4}
            value={decisionNotes}
            onChange={(e) => setDecisionNotes(e.target.value)}
            placeholder="Optional notes…"
            disabled={submittingDecision}
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              onClick={() => {
                setDecisionModalVisible(false);
                setDecisionNotes("");
                setPendingDecision(null);
              }}
              disabled={submittingDecision}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={submitDecision}
              loading={submittingDecision}
              danger={pendingDecision === "rejected"}
              style={
                pendingDecision === "accepted"
                  ? { backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }
                  : undefined
              }
            >
              {pendingDecision === "accepted" ? "Accept petition" : "Reject petition"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PetitionCommittee;
